import type {
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import { $applyNodeReplacement, DecoratorNode } from 'lexical'
import type { JSX } from 'react'
import * as React from 'react'

const ImageComponent = React.lazy(() => import('../component/ImageComponent.tsx'))

export type ImagePayload = {
  src: string
  altText?: string
  height?: number
  width?: number
  file?: File
  key?: NodeKey

  subpath?: string
  s3Key?: string
}

export type SerializedImageNode = Spread<
  {
    src: string
    altText?: string
    height?: number
    width?: number
    key?: NodeKey

    subpath?: string
    s3Key?: string
  },
  SerializedLexicalNode
>

function $convertImageElement(domNode: Node): DOMConversionOutput {
  const img = domNode as HTMLImageElement
  const { alt: altText, src, width, height } = img
  const node = $createImageNode({ altText, src, height, width })
  return { node }
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string
  __altText: string
  __width: number | 'auto'
  __height: number | 'auto'
  __file?: File

  __s3Key?: string

  constructor(
    src: string,
    altText: string = '',
    width: number | 'auto' = 'auto',
    height: number | 'auto' = 'auto',
    file?: File,
    key?: NodeKey,

    s3Key?: string
  ) {
    super(key)
    this.__src = src
    this.__altText = altText
    this.__width = width
    this.__height = height
    this.__file = file

    this.__s3Key = s3Key
  }

  static getType(): string {
    return 'image'
  }

  setSrc(src: string) {
    const writable = this.getWritable()
    writable.__src = src
  }

  setS3Key(s3Key?: string) {
    const writable = this.getWritable()
    writable.__s3Key = s3Key
  }

  getS3Key(): string | undefined {
    return this.__s3Key
  }

  getFile(): File | undefined {
    return this.__file
  }

  setFile(file: File | undefined) {
    const writable = this.getWritable()
    writable.__file = file
  }

  static clone(node: ImageNode) {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__file,
      node.__key,
      node.__s3Key
    )
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, height, width, key, subpath, s3Key } = serializedNode
    return $createImageNode({
      src,
      altText,
      height,
      width,
      key,
      subpath,
      s3Key,
    }).updateFromJSON(serializedNode)
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (_node: Node) => ({
        conversion: $convertImageElement,
        priority: 0,
      }),
    }
  }

  exportJSON() {
    return {
      type: 'image',
      src: this.__src,
      width: this.__width,
      height: this.__height,
      s3Key: this.__s3Key,
      version: 1,
    }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span')
    const theme = config.theme
    const className = theme.image
    if (className !== undefined) {
      span.className = className
    }
    return span
  }

  updateDOM(prevNode: ImageNode, _dom: HTMLElement): boolean {
    return (
      this.__src !== prevNode.__src ||
      this.__altText !== prevNode.__altText ||
      this.__width !== prevNode.__width ||
      this.__height !== prevNode.__height
    )
  }

  setWidthAndHeight(width: number, height: number) {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
  }

  // decorate: render React component
  decorate() {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        nodeKey={this.getKey()}
      />
    )
  }
}

export function $createImageNode({
  src,
  altText,
  height,
  width,
  file,
  key,
  s3Key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, altText, height, width, file, key, s3Key))
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode
}
